import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export default function AgencyPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadPackages = () => {
    setLoading(true);
    api.get('/api/agency-crm/packages')
      .then(data => setPackages(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadPackages();
  }, []);

  const handleAddPackage = async (e) => {
    e.preventDefault();
    setSaving(true);
    const form = e.target;
    const body = {
      packageName: form.packageName.value.trim(),
      price: Number(form.price.value) || 0,
      duration: Number(form.duration.value) || 1,
      services: form.services.value.trim()
    };

    try {
      await api.post('/api/agency-crm/packages', body);
      setShowAddForm(false);
      loadPackages();
    } catch (err) {
      alert('Failed to save package: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package template?')) return;
    try {
      await api.del(`/api/agency-crm/packages/${id}`);
      loadPackages();
    } catch (err) {
      alert('Failed to delete package: ' + err.message);
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading CRM Package templates…</div>;
  }

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontWeight: 900, fontSize: '1.4rem' }}>CRM Package Builder</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#64748b' }}>Design predefined packages containing multiple services</p>
        </div>
        <button onClick={() => setShowAddForm(prev => !prev)} className="cm-btn-emerald">
          {showAddForm ? 'Cancel' : '+ Create Package Template'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddPackage} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 24, marginBottom: 24, maxWidth: 580 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', fontWeight: 800 }}>New Package Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 12 }}>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Package Name *</label>
              <input name="packageName" type="text" required placeholder="e.g. Growth Package" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
            <div className="cm-field-group">
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Price (₹) *</label>
              <input name="price" type="number" required placeholder="e.g. 75000" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            </div>
          </div>
          <div className="cm-field-group" style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Duration (Months) *</label>
            <input name="duration" type="number" required defaultValue="3" min="1" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
          </div>
          <div className="cm-field-group" style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, marginBottom: 4 }}>Included Services (Format: `Service Name:Qty, Service Name:Qty`)*</label>
            <input name="services" type="text" required placeholder="e.g. Reels Creation:15, Graphic Design:60, Meta Ads:3" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0' }} />
            <span style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4, display: 'block' }}>💡 Comma separated services. Example: `Reels Creation:15` means 15 Reels in total.</span>
          </div>

          <button type="submit" className="cm-btn-emerald" disabled={saving}>
            {saving ? 'Creating…' : 'Save Package'}
          </button>
        </form>
      )}

      {/* Packages Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
        {packages.map(p => (
          <div key={p.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16, padding: 22, boxShadow: '0 4px 12px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: '1.2rem', fontWeight: 900, color: '#0f172a' }}>{p.packageName}</span>
                <span style={{ background: '#224eae', color: '#fff', padding: '3px 8px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 800 }}>{p.duration} Months</span>
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#16a34a', marginBottom: 18 }}>₹{p.price.toLocaleString()}</div>
              
              <div style={{ fontSize: '0.85rem', color: '#64748b', borderTop: '1px dashed #f1f5f9', paddingTop: 12 }}>
                <strong>Included Services:</strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {p.services.split(',').map((s, idx) => (
                    <div key={idx} style={{ padding: '6px 10px', background: '#f8fafc', borderRadius: 8, fontSize: '0.8rem', border: '1px solid #f1f5f9' }}>
                      ✔️ {s.trim()}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => handleDeletePackage(p.id)}
              style={{ background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: '0.8rem', fontWeight: 800, cursor: 'pointer', marginTop: 20, width: '100%' }}
            >
              Remove Template
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
